import React, { useState } from 'react';
import { Copy, Download, Upload, Trash2, CheckCircle } from 'lucide-react';

// Simple Header component since we don't have the original
const Header = () => (
    <div className="bg-white shadow-sm border-b border-gray-200 mb-6">
        <div className="max-w-6xl mx-auto px-4 py-3">
            <h1 className="text-lg font-semibold text-gray-800">JSON Tools</h1>
        </div>
    </div>
);

export default function JsonReferralExtractor() {
    const [jsonInput, setJsonInput] = useState('');
    const [urls, setUrls] = useState([]);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);

    const extractUrls = () => {
        try {
            setError('');
            let data;

            // Clean and prepare the input
            let cleanedInput = jsonInput.trim();

            // Remove any leading/trailing non-JSON characters
            cleanedInput = cleanedInput.replace(/^[^{\[]*/, '').replace(/[^}\]]*$/, '');

            if (!cleanedInput) {
                throw new Error('No valid JSON content found');
            }

            // Try to auto-fix common JSON issues
            const fixJsonIssues = (jsonStr) => {
                let fixed = jsonStr;

                // If it looks like multiple objects without array brackets, wrap them
                if (fixed.startsWith('{') && !fixed.startsWith('[')) {
                    // Count opening and closing braces to detect multiple objects
                    const objects = [];
                    let braceCount = 0;
                    let start = 0;

                    for (let i = 0; i < fixed.length; i++) {
                        if (fixed[i] === '{') braceCount++;
                        if (fixed[i] === '}') braceCount--;

                        if (braceCount === 0 && fixed[i] === '}') {
                            objects.push(fixed.substring(start, i + 1));
                            // Find next opening brace
                            for (let j = i + 1; j < fixed.length; j++) {
                                if (fixed[j] === '{') {
                                    start = j;
                                    i = j - 1;
                                    break;
                                }
                            }
                        }
                    }

                    if (objects.length > 1) {
                        fixed = '[' + objects.join(',') + ']';
                    } else if (objects.length === 1) {
                        fixed = objects[0];
                    }
                }

                // Remove trailing commas before closing brackets/braces
                fixed = fixed.replace(/,(\s*[}\]])/g, '$1');

                // If it's not wrapped in array brackets but contains multiple objects, try wrapping
                if (!fixed.startsWith('[') && (fixed.match(/{/g) || []).length > 1) {
                    // Try wrapping in array brackets
                    const testJson = '[' + fixed + ']';
                    try {
                        JSON.parse(testJson);
                        fixed = testJson;
                    } catch (e) {
                        // If wrapping doesn't work, keep original
                    }
                }

                return fixed;
            };

            cleanedInput = fixJsonIssues(cleanedInput);

            // Try parsing with different approaches
            let parseError;

            // Approach 1: Direct parsing
            try {
                data = JSON.parse(cleanedInput);
            } catch (err) {
                parseError = err;

                // Approach 2: Try wrapping single object in array
                if (cleanedInput.startsWith('{') && !cleanedInput.startsWith('[')) {
                    try {
                        data = [JSON.parse(cleanedInput)];
                    } catch (err2) {
                        // Approach 3: Try parsing as array
                        try {
                            data = JSON.parse('[' + cleanedInput + ']');
                        } catch (err3) {
                            throw parseError; // Use original error
                        }
                    }
                } else {
                    throw parseError;
                }
            }

            // Extract URLs from referralHistory and remove wf- prefix
            const extractedUrls = [];

            const extractFromItem = (item) => {
                if (item && typeof item === 'object') {
                    if (item.referralHistory && Array.isArray(item.referralHistory)) {
                        item.referralHistory.forEach(url => {
                            if (typeof url === 'string') {
                                // Remove wf- prefix if it exists
                                const cleanedUrl = url.replace(/^https?:\/\/wf-/, 'https://');
                                extractedUrls.push(cleanedUrl);
                            }
                        });
                    }
                    // Recursively check nested objects
                    Object.values(item).forEach(value => {
                        if (Array.isArray(value)) {
                            value.forEach(extractFromItem);
                        } else if (typeof value === 'object' && value !== null) {
                            extractFromItem(value);
                        }
                    });
                }
            };

            if (Array.isArray(data)) {
                data.forEach(extractFromItem);
            } else {
                extractFromItem(data);
            }

            setUrls(extractedUrls);

            if (extractedUrls.length === 0) {
                setError('No "referralHistory" fields found in the JSON data');
            }

        } catch (err) {
            setError(`Error parsing JSON: ${err.message}. Please check your JSON format or try the "Fix JSON" button.`);
            setUrls([]);
        }
    };

    const copyAllUrls = async () => {
        if (urls.length === 0) return;

        try {
            await navigator.clipboard.writeText(urls.join('\n'));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy URLs:', err);
        }
    };

    const copyUrl = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
        } catch (err) {
            console.error('Failed to copy URL:', err);
        }
    };

    const downloadUrls = () => {
        if (urls.length === 0) return;

        const blob = new Blob([urls.join('\n')], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'extracted-referral-urls.txt';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const downloadCsv = () => {
        if (urls.length === 0) return;

        const csvContent = urls.join(',');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'extracted-referral-urls.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const copyCsv = async () => {
        if (urls.length === 0) return;

        try {
            await navigator.clipboard.writeText(urls.join(','));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy CSV:', err);
        }
    };

    const clearAll = () => {
        setJsonInput('');
        setUrls([]);
        setError('');
    };

    const autoFixJson = () => {
        let fixed = jsonInput.trim();

        // Remove any text before first { or [
        fixed = fixed.replace(/^[^{\[]*/, '');

        // Remove any text after last } or ]
        fixed = fixed.replace(/[^}\]]*$/, '');

        // If it looks like multiple objects separated by commas but not in array
        if (fixed.includes('},{') && !fixed.startsWith('[')) {
            fixed = '[' + fixed + ']';
        }

        // Remove trailing commas
        fixed = fixed.replace(/,(\s*[}\]])/g, '$1');

        // Try to add missing array brackets for multiple objects
        if (!fixed.startsWith('[') && fixed.includes('}\s*{')) {
            fixed = '[' + fixed + ']';
        }

        setJsonInput(fixed);
        setError('JSON has been auto-fixed. Try extracting URLs now.');
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setJsonInput(e.target.result);
            };
            reader.readAsText(file);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
            <Header />
            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 mb-2">JSON Referral History Extractor</h1>
                    <p className="text-gray-600">Extract all URLs from "referralHistory" fields and remove wf- prefix</p>
                </div>

                <div className="grid lg:grid-cols-2 gap-6">
                    {/* Input Section */}
                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold text-gray-800">JSON Input</h2>
                            <div className="flex gap-2">
                                <label className="cursor-pointer bg-blue-500 text-white px-3 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 text-sm">
                                    <Upload size={16} />
                                    Upload File
                                    <input
                                        type="file"
                                        accept=".json,.txt"
                                        onChange={handleFileUpload}
                                        className="hidden"
                                    />
                                </label>
                                <button
                                    onClick={clearAll}
                                    className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2 text-sm"
                                >
                                    <Trash2 size={16} />
                                    Clear
                                </button>
                            </div>
                        </div>

                        <textarea
                            value={jsonInput}
                            onChange={(e) => setJsonInput(e.target.value)}
                            placeholder="Paste your JSON data here..."
                            className="w-full h-64 p-4 border border-gray-300 rounded-lg font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />

                        <div className="flex justify-between items-center mt-4">
                            <div className="flex gap-2">
                                <button
                                    onClick={extractUrls}
                                    disabled={!jsonInput.trim()}
                                    className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                                >
                                    Extract URLs
                                </button>
                                <button
                                    onClick={autoFixJson}
                                    disabled={!jsonInput.trim()}
                                    className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
                                >
                                    Fix JSON
                                </button>
                            </div>

                            {error && (
                                <div className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg max-w-md">
                                    {error}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Output Section */}
                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold text-gray-800">
                                Extracted URLs ({urls.length})
                            </h2>

                            {urls.length > 0 && (
                                <div className="flex gap-2">
                                    <button
                                        onClick={copyAllUrls}
                                        className="bg-blue-500 text-white px-3 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 text-sm"
                                    >
                                        {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
                                        Copy (Lines)
                                    </button>
                                    <button
                                        onClick={copyCsv}
                                        className="bg-teal-500 text-white px-3 py-2 rounded-lg hover:bg-teal-600 transition-colors flex items-center gap-2 text-sm"
                                    >
                                        <Copy size={16} />
                                        Copy CSV
                                    </button>
                                    <button
                                        onClick={downloadUrls}
                                        className="bg-purple-500 text-white px-3 py-2 rounded-lg hover:bg-purple-600 transition-colors flex items-center gap-2 text-sm"
                                    >
                                        <Download size={16} />
                                        .txt
                                    </button>
                                    <button
                                        onClick={downloadCsv}
                                        className="bg-orange-500 text-white px-3 py-2 rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2 text-sm"
                                    >
                                        <Download size={16} />
                                        .csv
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="h-64 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
                            {urls.length === 0 ? (
                                <div className="text-gray-500 text-center mt-8">
                                    No URLs extracted yet. Paste your JSON and click "Extract URLs".
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {urls.map((url, index) => (
                                        <div
                                            key={index}
                                            className="bg-white p-3 rounded-lg border border-gray-200 group hover:shadow-sm transition-shadow"
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-xs text-gray-500 mb-1">URL #{index + 1}</div>
                                                    <div className="text-sm text-blue-600 break-all font-mono hover:text-blue-800">
                                                        <a href={url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                                                            {url}
                                                        </a>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => copyUrl(url)}
                                                    className="opacity-0 group-hover:opacity-100 bg-gray-100 hover:bg-gray-200 p-1 rounded transition-all"
                                                    title="Copy this URL"
                                                >
                                                    <Copy size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Instructions */}
                <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">How to Use</h3>
                    <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
                        <div>
                            <h4 className="font-medium text-gray-800 mb-2">What it does:</h4>
                            <ul className="space-y-1">
                                <li>• Finds all "referralHistory" arrays in your JSON</li>
                                <li>• Extracts URLs from these arrays</li>
                                <li>• Removes "wf-" prefix from domains (e.g., wf-instagram.com → instagram.com)</li>
                                <li>• Works with nested objects and arrays</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-medium text-gray-800 mb-2">Export Options:</h4>
                            <ul className="space-y-1">
                                <li>• <strong>Copy (Lines):</strong> Each URL on a new line</li>
                                <li>• <strong>Copy CSV:</strong> All URLs comma-separated</li>
                                <li>• <strong>.txt:</strong> Download as text file (line-separated)</li>
                                <li>• <strong>.csv:</strong> Download as CSV file (comma-separated)</li>
                            </ul>
                        </div>
                    </div>
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                        <h4 className="font-medium text-blue-800 mb-2">Example JSON Structure:</h4>
                        <pre className="text-xs text-blue-700 overflow-x-auto">
                            {`{
  "address": "...",
  "referralHistory": [
    "https://wf-instagram.com/p/C-AKObFtw0e"
  ]
}`}
                        </pre>
                        <p className="text-xs text-blue-600 mt-2">Will extract: https://instagram.com/p/C-AKObFtw0e</p>
                    </div>
                </div>
            </div>
        </div>
    );
}